export function formatNumber(value) {
  return new Intl.NumberFormat("pt-BR").format(value);
}

export function onlyNumbers(value) {
  return value ? String(value).replace(/\D/g, "") : "";
}

export function formatValue(value) {
  const normalized = value == null ? "" : String(value).trim();
  return normalized || "Não informado";
}

export function normalizeText(value) {
  return value == null ? "" : String(value).trim();
}

export function resolveAccountType(cpfCnpj) {
  const digits = onlyNumbers(cpfCnpj);

  if (digits.length === 11) return "Pessoa física";
  if (digits.length === 14) return "Empresa";

  return "Não informado";
}

export function formatAddress(address = {}) {
  const parts = [
    address.street,
    address.number,
    address.neighborhood,
    address.city,
    address.state,
  ].filter(Boolean);

  return parts.filter(Boolean).join(" · ");
}


export function resolveAccountLogo(profile) {
  return (
    profile?.account_logo_url ||
    profile?.logo_url ||
    profile?.logoUrl ||
    profile?.accountLogoUrl ||
    ""
  );
}

export function buildAccountShellSummary(profile, accountId) {
  const profilePermissions =
    profile?.permissions && typeof profile.permissions === "object"
      ? profile.permissions
      : null;

  return {
    id: accountId || profile?.account_id || profile?.id || "",
    isAccountOwner: Boolean(profile?.is_account_owner || profile?.isAccountOwner),
    logoUrl: resolveAccountLogo(profile),
    name: resolveProfileName(profile),
    status: profile?.account_status || profile?.accountStatus || profile?.status || "",
    permissions: profilePermissions,
    permissionsLoaded: Boolean(
      Object.prototype.hasOwnProperty.call(profile || {}, "permissions") ||
      Object.prototype.hasOwnProperty.call(profile || {}, "permission_levels") ||
      Object.prototype.hasOwnProperty.call(profile || {}, "permission_slugs") ||
      Object.prototype.hasOwnProperty.call(profile || {}, "capabilities")
    ),
  };
}

export function getImageErrorMessage(error) {
  const code = String(error?.code || error?.error || "").toLowerCase();
  const message = String(error?.message || error?.error || "").toLowerCase();

  if (code === "logo_too_large" || code === "avatar_too_large" || message.includes("too large")) {
    return "A imagem deve ter no máximo 1MB.";
  }

  if (
    code === "invalid_image_format" ||
    code === "invalid_image_payload" ||
    message.includes("invalid image")
  ) {
    return "Envie uma imagem em PNG.";
  }

  return "Não foi possível atualizar a logo.";
}

export function splitAccountName(value) {
  const accountName = String(value || "").trim();
  const match = accountName.match(/^(.+?)\s*\((.+)\)\s*$/);

  return {
    companyName: String(match?.[1] || accountName).trim(),
    responsibleName: String(match?.[2] || "").trim(),
  };
}

export function isUsableAccountName(value, profile) {
  const name = normalizeText(value);
  const username = normalizeText(profile?.username);
  const normalizedName = name.toLocaleLowerCase("pt-BR");

  if (!name) return false;
  if (username && name === username) return false;
  if (["usuario", "usuário", "user"].includes(normalizedName)) return false;

  return true;
}

export function getAccountNameCandidates(profile) {
  return [
    profile?.trade_name,
    profile?.legal_name,
    profile?.account_name,
    profile?.name,
  ];
}

export function resolveProfileName(profile) {
  const candidates = getAccountNameCandidates(profile);

  for (const candidate of candidates) {
    const { companyName } = splitAccountName(candidate);

    if (isUsableAccountName(companyName, profile)) return companyName;
  }

  return "";
}

export function resolveResponsibleName(profile) {
  const directResponsibleName = normalizeText(
    profile?.contact_name || profile?.responsible_name
  );

  if (directResponsibleName) return directResponsibleName;

  const candidates = getAccountNameCandidates(profile);

  for (const candidate of candidates) {
    const { responsibleName } = splitAccountName(candidate);

    if (responsibleName) return responsibleName;
  }

  return "";
}

export function buildAccountInfo(profile) {
  const address = profile?.address || {};
  const profileName = resolveProfileName(profile);
  const responsibleName = resolveResponsibleName(profile);
  const accountInfo = [
    { label: "Nome da conta", value: formatValue(profileName) },
    { label: "CNPJ", value: formatValue(profile?.cpf_cnpj) },
    { label: "Tipo de conta", value: resolveAccountType(profile?.cpf_cnpj) },
    { label: "E-mail de acesso", value: formatValue(profile?.email) },
    { label: "E-mail de contato", value: formatValue(address.contact_email) },
    { label: "WhatsApp", value: formatValue(address.cellphone) },
    { label: "Telefone comercial", value: formatValue(address.telephone) },
    { label: "Endereço", value: formatValue(formatAddress(address)), wide: true },
  ];

  if (responsibleName) {
    accountInfo.splice(1, 0, {
      label: "Responsável",
      value: responsibleName,
    });
  }

  return accountInfo;
}

export function buildAccountForm(profile) {
  const address = profile?.address || {};
  const digits = onlyNumbers(profile?.cpf_cnpj);
  const profileName = resolveProfileName(profile);
  const responsibleName = resolveResponsibleName(profile);

  return {
    person_type: digits.length === 11 ? "fisica" : "PJ",
    firstName: profileName,
    lastName: responsibleName,
    cpf_cnpj: profile?.cpf_cnpj || "",
    email: address.contact_email || profile?.email || "",
    telephone: address.telephone || "",
    cellphone: address.cellphone || "",
    zipcode: address.postal_code || "",
    street: address.street || "",
    number: address.number || "",
    neighborhood: address.neighborhood || "",
    city: address.city || "",
    state: address.state || "",
    country: address.country || "BR",
  };
}

export function buildAccountUpdatePayload(form) {
  const accountName = normalizeText(form.firstName);
  const responsibleName = normalizeText(form.lastName);

  return {
    person_type: form.person_type,
    trade_name: accountName,
    legal_name: accountName,
    responsible_name: responsibleName,
    contact_name: responsibleName,
    cpf_cnpj: form.cpf_cnpj,
    telephone: form.telephone,
    cellphone: form.cellphone,
    email: form.email,
    zipcode: form.zipcode,
    street: form.street,
    number: form.number,
    neighborhood: form.neighborhood,
    city: form.city,
    state: form.state,
    country: form.country,
  };
}

export function pluralizeAvailable(value) {
  return `${value} ${value === 1 ? "disponível" : "disponíveis"}`;
}

export function formatQuotaLimit(limit) {
  if (limit == null) return "não informado";
  return limit < 0 ? "ilimitado" : formatNumber(limit);
}

export function formatQuotaAvailable(available) {
  if (available == null) return "Não informado";
  return available < 0 ? "ilimitados" : pluralizeAvailable(available);
}
